import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import Spinner from '@atlaskit/spinner/spinner';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import {
	getExperienceName,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceFailure } from '@confluence/macro-tracker/entry-points/MacroExperienceFailure.ts';
import { MacroExperienceSuccess } from '@confluence/macro-tracker/entry-points/MacroExperienceSuccess.ts';

import { PageIncludeContent } from './components/PageIncludeContent.tsx';
import { PageIncludeError } from './components/PageIncludeError.tsx';
import { usePageIncludeMacroParams } from './hooks/usePageIncludeMacroParams.ts';
import { usePageIncludeMacroQuery } from './hooks/usePageIncludeMacroQuery.ts';
import { usePageIncludeStack } from './hooks/usePageIncludeStack.ts';
import { useSerializedMacroParameters } from './hooks/useSerializedMacroParameters.ts';
import { i18n } from './pageIncludeMacroI18n.ts';
import type {
	PageIncludeMacroParams,
	PageIncludeRendererWrapperProps,
} from './pageIncludeMacroTypes.ts';

export type PageIncludeMacroRendererProps = {
	macroDefaultProps: ExtensionHandlerProps;
	macroParams?: PageIncludeMacroParams;
	/**
	 * Renderer wrapper for rendering the included page body. Classic/editor and SSR callers provide
	 * context-specific wrappers while this package owns target resolution, errors, circular checks,
	 * and macro attrs.
	 */
	RendererWrapper: React.ComponentType<PageIncludeRendererWrapperProps>;
};

const PageIncludeMacroRendererComponent = ({
	macroDefaultProps,
	macroParams,
	RendererWrapper,
}: PageIncludeMacroRendererProps) => {
	const { formatMessage } = useIntl();
	const { contentId, extensionKey, mode, node } = macroDefaultProps;
	const attributes = macroDefaultProps.attributes ?? getMacroAttributesFromADFNode(node);
	const experienceName = getExperienceName(mode, node);
	const { parsedTarget, macroId, hostContentId } = usePageIncludeMacroParams({
		macroDefaultProps,
		macroParams,
	});
	const serializedMacroParameters = useSerializedMacroParameters(macroParams);
	const { getCircularEntry } = usePageIncludeStack();

	const {
		loading,
		error: queryError,
		resolvedTarget,
		adfString,
		mediaToken,
	} = usePageIncludeMacroQuery(parsedTarget, macroDefaultProps.spaceKey);

	if (parsedTarget.error === 'missing-target') {
		const missingTargetError = new Error(formatMessage(i18n.noTitle));
		return (
			<>
				<MacroExperienceFailure
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={attributes}
					error={missingTargetError}
				/>
				<PageIncludeError
					heading={<FormattedMessage {...i18n.unableToRender} />}
					body={<FormattedMessage {...i18n.noTitle} />}
				/>
			</>
		);
	}

	if (queryError === 'invalid-content-type') {
		const invalidContentTypeError = new Error(formatMessage(i18n.invalidContentType));
		return (
			<>
				<MacroExperienceFailure
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={attributes}
					error={invalidContentTypeError}
				/>
				<PageIncludeError
					heading={<FormattedMessage {...i18n.unableToRender} />}
					body={<FormattedMessage {...i18n.invalidContentType} />}
				/>
			</>
		);
	}

	if (loading) {
		return (
			<Box padding="space.300" testId="page-include-loading">
				<Stack alignInline="center">
					<Spinner size="medium" label={formatMessage(i18n.loading)} />
				</Stack>
			</Box>
		);
	}

	if (queryError === 'not-found' || queryError === 'network-failure' || !resolvedTarget) {
		const notFoundError = new Error(formatMessage(i18n.notFound));
		return (
			<>
				<MacroExperienceFailure
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={attributes}
					error={notFoundError}
				/>
				<PageIncludeError
					heading={<FormattedMessage {...i18n.unableToRender} />}
					body={<FormattedMessage {...i18n.notFound} />}
				/>
			</>
		);
	}

	const circularEntry = getCircularEntry(resolvedTarget.contentId);
	if (circularEntry) {
		const circularIncludeError = new Error(
			formatMessage(i18n.alreadyIncluded, { pageTitle: circularEntry.title }),
		);
		return (
			<>
				<MacroExperienceFailure
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={attributes}
					error={circularIncludeError}
				/>
				<PageIncludeError
					heading={<FormattedMessage {...i18n.unableToRender} />}
					body={
						<FormattedMessage
							{...i18n.alreadyIncluded}
							values={{ pageTitle: circularEntry.title }}
						/>
					}
				/>
			</>
		);
	}

	if (!adfString) {
		const noAdfError = new Error(formatMessage(i18n.notFound));
		return (
			<>
				<MacroExperienceFailure
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={attributes}
					error={noAdfError}
				/>
				<PageIncludeError
					heading={<FormattedMessage {...i18n.unableToRender} />}
					body={<FormattedMessage {...i18n.notFound} />}
				/>
			</>
		);
	}

	return (
		<>
			<MacroExperienceSuccess
				name={experienceName}
				contentId={contentId}
				extensionKey={extensionKey}
				mode={mode}
				attributes={attributes}
			/>
			<div
				data-fabric-macro={macroId}
				data-macro-body=""
				data-macro-parameters={serializedMacroParameters}
				data-testid="page-include-macro"
				data-content-id={hostContentId}
			>
				<PageIncludeContent
					resolvedTarget={resolvedTarget}
					adfString={adfString}
					mediaToken={mediaToken}
					macroId={macroId}
					RendererWrapper={RendererWrapper}
				/>
			</div>
		</>
	);
};

const renderErrorFallback = () => <FormattedMessage {...i18n.genericError} />;

export const PageIncludeMacroRenderer = (
	props: PageIncludeMacroRendererProps,
): React.JSX.Element => (
	<GenericErrorBoundary
		renderOnError={renderErrorFallback}
		attribution={Attribution.PAGE_EXPERIENCES}
	>
		<PageIncludeMacroRendererComponent {...props} />
	</GenericErrorBoundary>
);
